import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
  Chip,
  Box,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { api } from '../services/authService';

const CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'voice', label: 'Voice' },
  { value: 'email', label: 'Email' },
  { value: 'social', label: 'Social Media' },
];

const CommunicationsTab = ({ accountId, showSnackbar }) => {
  const [contactEndpoints, setContactEndpoints] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [newEndpointDialogOpen, setNewEndpointDialogOpen] = useState(false);
  const [editEndpointDialogOpen, setEditEndpointDialogOpen] = useState(false);
  const [campaignAssignmentDialogOpen, setCampaignAssignmentDialogOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [selectedEndpointForCampaigns, setSelectedEndpointForCampaigns] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [newEndpoint, setNewEndpoint] = useState({
    value: '',
    platform_id: null,
    label: '',
    priority: 'primary',
    is_primary: false,
    is_verified: false,
    account: null,
    campaign_ids: [],
    funnel: null,
    channels: []
  });

  useEffect(() => {
    fetchContactEndpoints();
    fetchCampaigns();
    fetchPlatforms();
  }, [accountId]);

  useEffect(() => {
    if (newEndpoint.campaign_ids.length > 0) {
      // Fetch funnels for the first selected campaign
      fetchFunnels(newEndpoint.campaign_ids[0]);
    } else {
      setFunnels([]);
    }
  }, [newEndpoint.campaign_ids]);

  useEffect(() => {
    if (editingEndpoint?.campaign_ids?.length > 0) {
      // Fetch funnels for the first selected campaign
      fetchFunnels(editingEndpoint.campaign_ids[0]);
    } else {
      setFunnels([]);
    }
  }, [editingEndpoint?.campaign_ids]);

  const fetchPlatforms = async () => {
    try {
      const response = await api.get('/communications/contact-endpoint-platforms/active/');
      setPlatforms(response.data);
    } catch (error) {
      console.error('Error fetching platforms:', error);
      showSnackbar('Error fetching platforms', 'error');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/campaigns/`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      showSnackbar('Error fetching campaigns', 'error');
    }
  };

  const fetchFunnels = async (campaignId) => {
    try {
      const response = await api.get(`/crm/campaigns/${campaignId}/funnels/`);
      setFunnels(response.data);
    } catch (error) {
      console.error('Error fetching funnels:', error);
      showSnackbar('Error fetching funnels', 'error');
    }
  };

  const fetchContactEndpoints = async () => {
    try {
      const response = await api.get('/communications/contact-endpoints/', {
        params: { account_id: accountId }
      });
      setContactEndpoints(response.data);
    } catch (error) {
      console.error('Error fetching contact endpoints:', error);
      showSnackbar('Error fetching contact endpoints', 'error');
    }
  };

  const handleCreateEndpoint = async () => {
    try {
      const payload = {
        ...newEndpoint,
        account: accountId,
        channels: newEndpoint.channels.map(channel => ({ channel }))
      };
      await api.post('/communications/contact-endpoints/', payload);
      setNewEndpointDialogOpen(false);
      setNewEndpoint({
        value: '',
        platform_id: null,
        label: '',
        priority: 'primary',
        is_primary: false,
        is_verified: false,
        account: null,
        campaign_ids: [],
        funnel: null,
        channels: []
      });
      fetchContactEndpoints();
      showSnackbar('Contact endpoint created successfully', 'success');
    } catch (error) {
      console.error('Error creating contact endpoint:', error);
      showSnackbar('Error creating contact endpoint', 'error');
    }
  };

  const handleEditEndpoint = async () => {
    try {
      const payload = {
        ...editingEndpoint,
        channels: editingEndpoint.channels.map(channel => ({ channel }))
      };
      await api.put(`/communications/contact-endpoints/${editingEndpoint.id}/`, payload);
      setEditEndpointDialogOpen(false);
      setEditingEndpoint(null);
      fetchContactEndpoints();
      showSnackbar('Contact endpoint updated successfully', 'success');
    } catch (error) {
      console.error('Error updating contact endpoint:', error);
      showSnackbar('Error updating contact endpoint', 'error');
    }
  };

  const handleDeleteEndpoint = async (endpointId) => {
    try {
      await api.delete(`/communications/contact-endpoints/${endpointId}/`);
      fetchContactEndpoints();
      showSnackbar('Contact endpoint deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting contact endpoint:', error);
      showSnackbar('Error deleting contact endpoint', 'error');
    }
  };

  const handleOpenEditDialog = (endpoint) => {
    setEditingEndpoint({
      ...endpoint,
      platform_id: endpoint.platform?.id || null,
      channels: endpoint.channel_list || [],
      campaign_ids: endpoint.campaign_ids || []
    });
    setEditEndpointDialogOpen(true);
  };

  const handleOpenCampaignAssignmentDialog = (endpoint) => {
    setSelectedEndpointForCampaigns(endpoint);
    setCampaignAssignmentDialogOpen(true);
  };

  const handleAddToCampaign = async (endpointId, campaignId) => {
    try {
      await api.post(`/communications/contact-endpoints/${endpointId}/add_to_campaign/`, {
        campaign_id: campaignId
      });
      fetchContactEndpoints();
      showSnackbar('Contact endpoint added to campaign successfully', 'success');
    } catch (error) {
      console.error('Error adding to campaign:', error);
      showSnackbar('Error adding to campaign', 'error');
    }
  };

  const handleRemoveFromCampaign = async (endpointId, campaignId) => {
    try {
      await api.post(`/communications/contact-endpoints/${endpointId}/remove_from_campaign/`, {
        campaign_id: campaignId
      });
      fetchContactEndpoints();
      showSnackbar('Contact endpoint removed from campaign successfully', 'success');
    } catch (error) {
      console.error('Error removing from campaign:', error);
      showSnackbar('Error removing from campaign', 'error');
    }
  };

  const handleCampaignChange = (e, isEditing = false) => {
    const campaignIds = e.target.value;
    if (isEditing) {
      setEditingEndpoint({
        ...editingEndpoint,
        campaign_ids: campaignIds,
        funnel: null
      });
    } else {
      setNewEndpoint({
        ...newEndpoint,
        campaign_ids: campaignIds,
        funnel: null
      });
    }
  };

  const handleFunnelChange = (e, isEditing = false) => {
    const funnelId = e.target.value;
    if (isEditing) {
      setEditingEndpoint({
        ...editingEndpoint,
        funnel: funnelId
      });
    } else {
      setNewEndpoint({
        ...newEndpoint,
        funnel: funnelId
      });
    }
  };

  const handleChannelChange = (event, isEditing = false) => {
    const {
      target: { value },
    } = event;
    
    if (isEditing) {
      setEditingEndpoint({
        ...editingEndpoint,
        channels: typeof value === 'string' ? value.split(',') : value,
      });
    } else {
      setNewEndpoint({
        ...newEndpoint,
        channels: typeof value === 'string' ? value.split(',') : value,
      });
    }
  };

  const getPlatformOptions = (channels) => {
    if (!channels || channels.length === 0) return platforms;
    
    const platformTypes = [];
    if (channels.includes('social')) platformTypes.push('social');
    if (channels.includes('email')) platformTypes.push('email');
    if (channels.includes('sms') || channels.includes('voice')) platformTypes.push('phone');
    
    return platforms.filter(platform => 
      platformTypes.includes(platform.platform_type) && platform.is_active
    );
  };

  const renderCampaignAndFunnelFields = (isEditing = false) => {
    const currentEndpoint = isEditing ? editingEndpoint : newEndpoint;
    const handleCampaignChangeWrapper = (e) => handleCampaignChange(e, isEditing);
    const handleFunnelChangeWrapper = (e) => handleFunnelChange(e, isEditing);

    return (
      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Campaigns</InputLabel>
          <Select
            multiple
            value={currentEndpoint?.campaign_ids || []}
            onChange={handleCampaignChangeWrapper}
            input={<OutlinedInput label="Campaigns" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((campaignId) => {
                  const campaign = campaigns.find(c => c.id === campaignId);
                  return (
                    <Chip key={campaignId} label={campaign?.name || campaignId} />
                  );
                })}
              </Box>
            )}
          >
            {campaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Funnel</InputLabel>
          <Select
            value={currentEndpoint?.funnel || ''}
            onChange={handleFunnelChangeWrapper}
            label="Funnel"
            disabled={!currentEndpoint?.campaign_ids?.length}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {funnels.map((funnel) => (
              <MenuItem key={funnel.id} value={funnel.id}>
                {funnel.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderCampaignAssignmentDialog = () => {
    if (!selectedEndpointForCampaigns) return null;

    const assignedCampaignIds = selectedEndpointForCampaigns.campaign_ids || [];
    const assignedCampaigns = campaigns.filter(campaign => 
      assignedCampaignIds.includes(campaign.id)
    );
    const availableCampaigns = campaigns.filter(campaign => 
      !assignedCampaignIds.includes(campaign.id)
    );

    return (
      <Dialog 
        open={campaignAssignmentDialogOpen} 
        onClose={() => setCampaignAssignmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Campaign Assignment - {selectedEndpointForCampaigns.value}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Assigned Campaigns */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Assigned Campaigns ({assignedCampaigns.length})
              </Typography>
              <List>
                {assignedCampaigns.map((campaign) => (
                  <ListItem key={campaign.id} divider>
                    <ListItemText
                      primary={campaign.name}
                      secondary={`Assigned: ${new Date().toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFromCampaign(selectedEndpointForCampaigns.id, campaign.id)}
                      >
                        Remove
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {assignedCampaigns.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No campaigns assigned" />
                  </ListItem>
                )}
              </List>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Available Campaigns */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Available Campaigns ({availableCampaigns.length})
              </Typography>
              <List>
                {availableCampaigns.map((campaign) => (
                  <ListItem key={campaign.id} divider>
                    <ListItemText primary={campaign.name} />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handleAddToCampaign(selectedEndpointForCampaigns.id, campaign.id)}
                      >
                        Add
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {availableCampaigns.length === 0 && (
                  <ListItem>
                    <ListItemText primary="All campaigns assigned" />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignAssignmentDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }} color="textPrimary">
        Communication Endpoints
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setNewEndpointDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Add Communication Endpoint
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell>Channels</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Campaigns</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contactEndpoints.map((endpoint) => (
              <TableRow key={endpoint.id}>
                <TableCell>{endpoint.value}</TableCell>
                <TableCell>
                  {endpoint.channel_list?.map((channel) => (
                    <Chip
                      key={channel}
                      label={channel}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>{endpoint.platform?.display_name || '-'}</TableCell>
                <TableCell>{endpoint.label || '-'}</TableCell>
                <TableCell>
                  {endpoint.campaigns && endpoint.campaigns.length > 0 ? (
                    <Box>
                      {endpoint.campaigns.slice(0, 2).map((campaign) => (
                        <Chip
                          key={campaign.id}
                          label={campaign.name}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                      {endpoint.campaigns.length > 2 && (
                        <Chip
                          label={`+${endpoint.campaigns.length - 2} more`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{endpoint.priority}</TableCell>
                <TableCell>
                  <Chip
                    label={endpoint.is_verified ? 'Verified' : 'Unverified'}
                    color={endpoint.is_verified ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Manage Campaigns">
                    <IconButton
                      edge="end"
                      aria-label="campaigns"
                      onClick={() => handleOpenCampaignAssignmentDialog(endpoint)}
                      sx={{ mr: 1 }}
                    >
                      <CampaignIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Endpoint">
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleOpenEditDialog(endpoint)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Endpoint">
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteEndpoint(endpoint.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New Contact Endpoint Dialog */}
      <Dialog open={newEndpointDialogOpen} onClose={() => setNewEndpointDialogOpen(false)}>
        <DialogTitle>Add New Communication Endpoint</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Channels</InputLabel>
            <Select
              multiple
              value={newEndpoint.channels}
              onChange={(e) => handleChannelChange(e, false)}
              input={<OutlinedInput label="Channels" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Value"
            fullWidth
            value={newEndpoint.value}
            onChange={(e) => setNewEndpoint({ ...newEndpoint, value: e.target.value })}
            placeholder={newEndpoint.channels.includes('email') ? 'email@example.com' : 
                       newEndpoint.channels.includes('social') ? '@username' : 
                       '+1234567890'}
          />

          {newEndpoint.channels.length > 0 && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={newEndpoint.platform_id || ''}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, platform_id: e.target.value })}
                label="Platform"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {getPlatformOptions(newEndpoint.channels).map((platform) => (
                  <MenuItem key={platform.id} value={platform.id}>
                    {platform.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            margin="dense"
            label="Label"
            fullWidth
            value={newEndpoint.label}
            onChange={(e) => setNewEndpoint({ ...newEndpoint, label: e.target.value })}
            placeholder="e.g., Work Phone, Personal Email"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={newEndpoint.priority}
              onChange={(e) => setNewEndpoint({ ...newEndpoint, priority: e.target.value })}
              label="Priority"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="backup">Backup</MenuItem>
            </Select>
          </FormControl>

          {renderCampaignAndFunnelFields()}

          <FormControlLabel
            control={
              <Checkbox
                checked={newEndpoint.is_primary}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, is_primary: e.target.checked })}
              />
            }
            label="Set as Primary"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewEndpointDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateEndpoint} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Endpoint Dialog */}
      <Dialog open={editEndpointDialogOpen} onClose={() => setEditEndpointDialogOpen(false)}>
        <DialogTitle>Edit Communication Endpoint</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Channels</InputLabel>
            <Select
              multiple
              value={editingEndpoint?.channels || []}
              onChange={(e) => handleChannelChange(e, true)}
              input={<OutlinedInput label="Channels" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Value"
            fullWidth
            value={editingEndpoint?.value || ''}
            onChange={(e) => setEditingEndpoint({ ...editingEndpoint, value: e.target.value })}
            placeholder={editingEndpoint?.channels?.includes('email') ? 'email@example.com' : 
                       editingEndpoint?.channels?.includes('social') ? '@username' : 
                       '+1234567890'}
          />

          {editingEndpoint?.channels?.length > 0 && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={editingEndpoint?.platform_id || ''}
                onChange={(e) => setEditingEndpoint({ ...editingEndpoint, platform_id: e.target.value })}
                label="Platform"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {getPlatformOptions(editingEndpoint.channels).map((platform) => (
                  <MenuItem key={platform.id} value={platform.id}>
                    {platform.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            margin="dense"
            label="Label"
            fullWidth
            value={editingEndpoint?.label || ''}
            onChange={(e) => setEditingEndpoint({ ...editingEndpoint, label: e.target.value })}
            placeholder="e.g., Work Phone, Personal Email"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={editingEndpoint?.priority || ''}
              onChange={(e) => setEditingEndpoint({ ...editingEndpoint, priority: e.target.value })}
              label="Priority"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="backup">Backup</MenuItem>
            </Select>
          </FormControl>

          {renderCampaignAndFunnelFields(true)}

          <FormControlLabel
            control={
              <Checkbox
                checked={editingEndpoint?.is_primary || false}
                onChange={(e) => setEditingEndpoint({ ...editingEndpoint, is_primary: e.target.checked })}
              />
            }
            label="Set as Primary"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEndpointDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditEndpoint} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Assignment Dialog */}
      {renderCampaignAssignmentDialog()}
    </>
  );
};

export default CommunicationsTab; 